
import React, { useState, useEffect, useRef } from 'react'
import { MessageCircle, Send, Bot, User, Loader2 } from 'lucide-react'
import {
  getAllMonumentaEmployees,
  getLeadershipFromHierarchy,
  getFinancialSummary,
  getClientProjectData,
  getClientRevenue,
  getProjectRevenue
} from '../lib/real-data-service'

// Questions from pergunta-chave.md - Only the 3 specific questions
const KEY_QUESTIONS = [
  "Qual foi a receita total do Banco do Brasil em 2025?",
  "Quantos projetos do Banco do Brasil foram executados de janeiro a julho?",
  "Qual projeto do Banco do Brasil teve a maior rentabilidade no período?"
]

interface Message {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface JanesChatbotProps {
  isOpen: boolean
  onClose: () => void
}

export default function JanesChatbot({ isOpen, onClose }: JanesChatbotProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: 'Olá! Eu sou a Manu, sua assistente de dados da Monumenta. Como posso te ajudar com informações sobre a empresa?',
      timestamp: new Date()
    }
  ])
  const [currentInput, setCurrentInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState<string>('')
  const [typingIndex, setTypingIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Load data asynchronously when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        // Pre-load data that might be needed for responses
        await Promise.all([
          getAllMonumentaEmployees(),
          getLeadershipFromHierarchy()
        ])
        setDataLoaded(true)
      } catch (error) {
        console.error('Error loading data for chatbot:', error)
        // Still allow chatbot to function with fallback responses
        setDataLoaded(true)
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen && !dataLoaded) {
      loadData()
    }
  }, [isOpen, dataLoaded])

  // Typing animation effect
  useEffect(() => {
    if (isTyping && selectedQuestion && typingIndex < selectedQuestion.length) {
      const timeout = setTimeout(() => {
        setCurrentInput(prev => prev + selectedQuestion[typingIndex])
        setTypingIndex(prev => prev + 1)
      }, 50 + Math.random() * 100) // Simulate human typing speed variation
      
      return () => clearTimeout(timeout)
    } else if (isTyping && typingIndex >= selectedQuestion.length) {
      setIsTyping(false)
    }
  }, [isTyping, selectedQuestion, typingIndex])

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      const newHeight = Math.min(textarea.scrollHeight, 120) // Max 120px (about 4 lines)
      textarea.style.height = `${newHeight}px`
    }
  }

  // Handle input change with auto-resize
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isTyping) {
      setCurrentInput(e.target.value)
      // Adjust height after state update
      setTimeout(adjustTextareaHeight, 0)
    }
  }

  // Adjust height when currentInput changes
  useEffect(() => {
    adjustTextareaHeight()
  }, [currentInput])

  // Start typing a random question
  const startRandomQuestion = () => {
    const randomQuestion = KEY_QUESTIONS[Math.floor(Math.random() * KEY_QUESTIONS.length)]
    setSelectedQuestion(randomQuestion)
    setCurrentInput('')
    setTypingIndex(0)
    setIsTyping(true)
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
    textareaRef.current?.focus()
  }

  // Generate AI response based on the question
  const generateResponse = (question: string): string => {
    const lowerQuestion = question.toLowerCase()
    
    try {
      // Question 1: "Qual foi a receita total do Banco do Brasil em 2025?"
      if (lowerQuestion.includes('receita') && lowerQuestion.includes('banco do brasil') && lowerQuestion.includes('2025')) {
        return `# 💰 Receita Banco do Brasil 2025

## 📊 Resultado Financeiro
**Receita Total:** R$ 4.799.206,27  
**Período:** Janeiro a Julho 2025  
**Status:** Cliente Estratégico ⭐

## 💡 Análise Estratégica

Este resultado supera todas as expectativas e confirma nossa estratégia de relacionamento de longo prazo.

## 🎯 Destaque
Excelente performance que posiciona o BB como **conta âncora** do nosso portfólio empresarial.`
      }

      // Question 2: "Quantos projetos do Banco do Brasil foram executados de janeiro a julho?"
      if (lowerQuestion.includes('projetos') && lowerQuestion.includes('banco do brasil') && (lowerQuestion.includes('janeiro') || lowerQuestion.includes('julho'))) {
        return `# 📊 Projetos Banco do Brasil
## Janeiro a Julho 2025

## 🎯 Resultado Geral
**Total Executado:** 388 projetos  
**Período:** 7 meses (Jan-Jul 2025)  

## 💡 Análise de Performance
Demonstramos **alta produtividade** e **demanda consistente** do cliente, mantendo uma média robusta de projetos entregues mensalmente.`
      }

      // Question 3: "Qual projeto do Banco do Brasil teve a maior rentabilidade no período?"
      if (lowerQuestion.includes('projeto') && lowerQuestion.includes('banco do brasil') && (lowerQuestion.includes('rentabilidade') || lowerQuestion.includes('maior'))) {
        return `# 🏆 Projeto de Maior Rentabilidade
## Banco do Brasil

## 🎯 Projeto Vencedor
**"Super St Pub. UEG"**  
*Super Campanha Publicitária UEG*



## 💡 Destaque Estratégico
Este projeto tornou-se **referência interna** para futuros trabalhos com o Banco do Brasil, estabelecendo novos padrões de eficiência e rentabilidade.`
      }

      // Default response for unrecognized questions
      return `# 🤖 Assistente Manu
## Análise de Consulta

Entendi sua pergunta sobre **"${question}"**.  

Atualmente estou configurada para responder especificamente sobre dados do **Banco do Brasil**.

## 💡 Perguntas Disponíveis

### 📊 Dados Financeiros
• **Receita total** do Banco do Brasil em 2025

### 📈 Gestão de Projetos  
• **Quantidade de projetos** executados (janeiro a julho)

### 🏆 Performance de Projetos
• **Projeto com maior rentabilidade** no período

## 🎯 Como Prosseguir
**Reformule sua pergunta** usando uma das opções acima ou clique em **"🎲 Pergunta"** para ver um exemplo prático.

Como posso ajudar com informações do Banco do Brasil?`

    } catch (error) {
      return `❌ **Erro na Análise:**

Desculpe, houve um problema ao processar sua pergunta. Nossos sistemas de dados estão temporariamente indisponíveis.

🔧 **Tente novamente em alguns momentos** ou reformule sua pergunta de forma mais específica.

Posso ajudar com informações gerais sobre a Monumenta enquanto isso!`
    }
  }

  // Handle sending message
  const handleSend = async () => {
    if (!currentInput.trim()) return

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: currentInput,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    const questionText = currentInput
    setCurrentInput('')
    setIsProcessing(true)
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }

    // Simulate processing delay
    setTimeout(() => {
      const response = generateResponse(questionText)
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, assistantMessage])
      setIsProcessing(false)
    }, 1500 + Math.random() * 1000)
  }

  // Handle key press - Enter sends, Shift+Enter creates new line
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content w-full max-w-2xl h-[600px] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand">
              <Bot className="w-7 h-7 text-brand-dark" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-white mb-1">Manu</h3>
              <p className="text-sm text-brand-gray-light font-body">Assistente de Dados Monumenta</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-icon text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
            >
              <div
                className={`
                  max-w-[80%] p-4 rounded-monumenta whitespace-pre-line shadow-monumenta transition-all duration-200 hover:shadow-monumenta-lg
                  ${message.type === 'user'
                    ? 'bg-gradient-to-br from-brand-primary to-brand-accent-600 text-brand-dark'
                    : 'bg-brand-dark-lighter border border-gray-700/50 text-white'
                  }
                `}
              >
                <div className="flex items-start space-x-3">
                  {message.type === 'assistant' && (
                    <Bot className="w-5 h-5 mt-1 flex-shrink-0 text-brand-primary" />
                  )}
                  {message.type === 'user' && (
                    <User className="w-5 h-5 mt-1 flex-shrink-0 text-brand-dark" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-body leading-relaxed">{message.content}</p>
                    <p className="text-xs opacity-70 mt-2 font-body">
                      {message.timestamp.toLocaleTimeString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {isProcessing && (
            <div className="flex justify-start animate-fade-in">
              <div className="bg-brand-dark-lighter border border-gray-700/50 text-white p-4 rounded-monumenta shadow-monumenta">
                <div className="flex items-center space-x-3">
                  <Bot className="w-5 h-5 text-brand-primary" />
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                    <span className="text-sm font-body">Manu está analisando...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-700/50 p-6 bg-brand-dark/50">
          <div className="flex space-x-3">
            <button
              onClick={startRandomQuestion}
              disabled={isTyping || isProcessing || isLoading}
              className="btn-ghost text-sm flex items-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTyping ? 'Digitando...' : '🎲 Pergunta'}
            </button>
            <div className="flex-1 flex space-x-3">
              <textarea
                ref={textareaRef}
                value={currentInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  isLoading ? 'Carregando dados...' :
                  isTyping ? 'Digitando pergunta...' :
                  'Digite sua pergunta... (Enter para enviar, Shift+Enter para nova linha)'
                }
                className="form-textarea flex-1 resize-none min-h-[44px] max-h-[120px]"
                disabled={isTyping || isProcessing || isLoading}
                rows={1}
                style={{ height: '44px' }}
              />
              <button
                onClick={handleSend}
                disabled={!currentInput.trim() || isTyping || isProcessing || isLoading}
                className="btn-primary p-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <p className="text-xs text-brand-gray mt-3 font-body">
            💡 <strong>Enter</strong> para enviar • <strong>Shift+Enter</strong> para nova linha • <strong>🎲 Pergunta</strong> para pergunta aleatória
          </p>
        </div>
      </div>
    </div>
  )
}